import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { X, Mail, Lock, User } from 'lucide-react';

const AuthModal = ({ isOpen, onClose }) => {
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const reset = () => { setEmail(''); setPassword(''); setConfirmPw(''); setFirstName(''); setLastName(''); setError(''); };

  const handleLogin = (e) => {
    e.preventDefault();
    const res = login(email, password);
    if (res.ok) { reset(); onClose(); }
    else setError(t(`auth.${res.error}`));
  };

  const handleRegister = (e) => {
    e.preventDefault();
    if (password !== confirmPw) { setError(t('auth.passwordMismatch')); return; }
    const res = register({ email, password, firstName, lastName });
    if (res.ok) { reset(); onClose(); }
    else setError(t(`auth.${res.error}`));
  };

  const switchMode = (m) => { setMode(m); setError(''); };

  const inputCls = 'w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors';

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold">{mode === 'login' ? t('auth.login') : t('auth.register')}</h2>
          <button onClick={onClose}><X size={20} className="text-[#484c55]/40 hover:text-[#484c55]" /></button>
        </div>

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="p-5 space-y-4">
          {mode === 'register' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
                <input required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={t('passenger.firstName')} className={inputCls} />
              </div>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
                <input required value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={t('passenger.lastName')} className={inputCls} />
              </div>
            </div>
          )}

          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.email')} className={inputCls} />
          </div>

          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
            <input type="password" required minLength={4} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.password')} className={inputCls} />
          </div>

          {mode === 'register' && (
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
              <input type="password" required minLength={4} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder={t('auth.confirmPassword')} className={inputCls} />
            </div>
          )}

          {error && <p className="text-xs text-[#c30909] font-medium">{error}</p>}

          <button type="submit" className="w-full bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-sm uppercase tracking-wider py-3 rounded-lg transition-colors">
            {mode === 'login' ? t('auth.loginBtn') : t('auth.registerBtn')}
          </button>
        </form>

        <div className="px-5 pb-5 text-center">
          {mode === 'login' ? (
            <p className="text-xs text-[#484c55]/60">
              {t('auth.noAccount')}{' '}
              <button onClick={() => switchMode('register')} className="text-[#c30909] font-semibold hover:underline">{t('auth.register')}</button>
            </p>
          ) : (
            <p className="text-xs text-[#484c55]/60">
              {t('auth.hasAccount')}{' '}
              <button onClick={() => switchMode('login')} className="text-[#c30909] font-semibold hover:underline">{t('auth.login')}</button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
